
import { useEffect, useState } from 'react'
import Editor, { useMonaco } from '@monaco-editor/react'

interface MonacoEditorProps {
    value: string
    onChange: (value: string) => void
    language?: string
    readOnly?: boolean
    className?: string
}

export function MonacoEditor({
    value,
    onChange,
    language = 'markdown',
    readOnly = false,
    className = ''
}: MonacoEditorProps) {
    const monaco = useMonaco()
    const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'))

    // Detect theme changes
    useEffect(() => {
        const observer = new MutationObserver(() => {
            setIsDark(document.documentElement.classList.contains('dark'))
        })

        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ['class']
        })

        return () => observer.disconnect()
    }, [])

    // Configure theme when monaco instance loads or theme changes
    useEffect(() => {
        if (monaco) {
            monaco.editor.defineTheme('orion-dark', {
                base: 'vs-dark',
                inherit: true,
                rules: [],
                colors: {
                    'editor.background': '#000000',
                    'editorGutter.background': '#000000', // Gutter black
                    'minimap.background': '#000000'       // Minimap black
                }
            })
            monaco.editor.defineTheme('orion-light', {
                base: 'vs',
                inherit: true,
                rules: [],
                colors: {
                    'editor.background': '#ffffff',
                    'editorGutter.background': '#ffffff',
                    'minimap.background': '#ffffff'
                }
            })
            monaco.editor.setTheme(isDark ? 'orion-dark' : 'orion-light')
        }
    }, [monaco, isDark])

    return (
        <div className={`h-full w-full overflow-hidden ${className}`}>
            <Editor
                height="100%"
                defaultLanguage={language}
                language={language}
                value={value}
                onChange={(newValue) => onChange(newValue || '')}
                theme={isDark ? 'orion-dark' : 'orion-light'} // Use custom theme directly
                options={{
                    readOnly,
                    minimap: { enabled: true },
                    fontSize: 14,
                    wordWrap: 'on',
                    scrollBeyondLastLine: false,
                    automaticLayout: true, // Critical for resizing with sidebar
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    padding: { top: 16, bottom: 16 },
                    lineNumbers: 'on',
                    renderLineHighlight: 'all',
                    smoothScrolling: true,
                    cursorBlinking: 'smooth',
                    cursorSmoothCaretAnimation: 'on'
                }}
            />
        </div>
    )
}
